import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Info, Plus, Trash2 } from "lucide-react";
import withholdingTaxAPI from "../../api/withholdingTaxAPI";
import Button from "../Button";
import ReturnPaymentGl from "./ReturnPaymentGl";
import MoneyInput from "../MoneyInput";
import {
  MONTHS, manilaToday, bir_date, money, statusChips, Chip, displayValue, ItemRow, Part, inputClass,
  TaxpayerSizeBanner, FiledBadge, PaymentDetails, paymentsFromView, paymentPayload, mapFieldErrors,
  FileConfirmModal, useReturnActions,
} from "./returnUi";
import { pesosToCentavos } from "../../utils/whtMoney";

// BIR Form 1601-C (January 2018 ENCS) for one client and year —
// docs/WHT_IMPLEMENTATION.md §7, D8. Items 14–25 come from the month's
// compensation rows; 13/13A, Schedule I (→ item 26), item 29 and the
// penalties are entered here. Filed returns are read-only; "Amend" starts a
// new version.

const SCHEDULE_ROWS_ON_FORM = 3;

const HEADER = [
  ["1", "For the Month (MM/YYYY)", "text"],
  ["2", "Amended Return?", "bool"],
  ["3", "Any Taxes Withheld?", "bool"],
  ["4", "Number of Sheet/s Attached", "text"],
  ["5", "ATC", "text"],
];
const PART_I = [
  ["6", "Taxpayer Identification Number (TIN)", "text"],
  ["7", "RDO Code", "text"],
  ["8", "Withholding Agent's Name", "text"],
  ["9", "Registered Address", "text"],
  ["9A", "ZIP Code", "text"],
  ["10", "Contact Number", "text"],
  ["11", "Category of Withholding Agent", "category"],
  ["12", "Email Address", "text"],
];
const ITEM_13 = "Are there payees availing of tax relief under Special Law or International Tax Treaty?";
const ITEM_13A = "If yes, specify";

export const PART_II = [
  ["14", "Total Amount of Compensation"],
  ["15", "Statutory Minimum Wage for Minimum Wage Earners (MWEs)"],
  ["16", "Holiday Pay, Overtime Pay, Night Shift Differential Pay, Hazard Pay (for MWEs only)"],
  ["17", "13th Month Pay and Other Benefits"],
  ["18", "De Minimis Benefits"],
  ["19", "SSS, GSIS, PHIC, HDMF Mandatory Contributions & Union Dues (employee's share only)"],
  ["20", "Other Non-Taxable Compensation (specify)"],
  ["21", "Total Non-Taxable Compensation (Sum of Items 15 to 20)"],
  ["22", "Total Taxable Compensation (Item 14 Less Item 21)"],
  ["23", "Less: Taxable compensation not subject to withholding tax (for employees, other than MWEs, receiving ₱250,000 & below for the year)"],
  ["24", "Net Taxable Compensation (Item 22 Less Item 23)"],
  ["25", "Total Taxes Withheld"],
  ["26", "Add/(Less): Adjustment of Taxes Withheld from Previous Month/s (From Part IV-Schedule 1, Item 4)"],
  ["27", "Taxes Withheld for Remittance (Sum of Items 25 and 26)"],
  ["28", "Less: Tax Remitted in Return Previously Filed, if this is an amended return"],
  ["29", "Other Remittances Made (specify)"],
  ["30", "Total Tax Remittances Made (Sum of Items 28 and 29)"],
  ["31", "Tax Still Due/(Over-remittance) (Item 27 Less Item 30)"],
  ["32", "Surcharge"],
  ["33", "Interest"],
  ["34", "Compromise"],
  ["35", "Total Penalties (Sum of Items 32 to 34)"],
  ["36", "TOTAL AMOUNT STILL DUE/(Over-remittance) (Sum of Items 31 and 35)"],
];
const PAYMENT_ROWS = [
  ["37", "cash", "Cash/Bank Debit Memo"],
  ["38", "check", "Check"],
  ["39", "tdm", "Tax Debit Memo"],
  ["40", "other", "Others (specify)"],
];
const PENALTY_FIELD = { 32: "surcharge", 33: "interest", 34: "compromise" };
const QUARTER_END = [3, 6, 9, 12];

let rowSeq = 0;
const newScheduleRow = (r = {}) => ({
  id: `s${++rowSeq}`,
  period: r.period || "",
  datePaid: r.datePaid || "",
  bank: r.bank || "",
  number: r.number || "",
  taxPaid: (r.taxPaidCentavos || 0) / 100,
  shouldBe: (r.shouldBeTaxDueCentavos || 0) / 100,
});
const adjustmentOf = (r) => (pesosToCentavos(r.shouldBe) || 0) - (pesosToCentavos(r.taxPaid) || 0);
export const attachedSheets = (n) => (n > SCHEDULE_ROWS_ON_FORM ? Math.ceil((n - SCHEDULE_ROWS_ON_FORM) / SCHEDULE_ROWS_ON_FORM) : 0);
const periodLabel = (p) => (p ? `${p.slice(5, 7)}/${p.slice(0, 4)}` : "—");

const draftFromView = (data) => ({
  payments: paymentsFromView(PAYMENT_ROWS, data.payment),
  penaltyAuto: data.penaltyAuto !== false,
  surcharge: (data.manualSurchargeCentavos || 0) / 100,
  interest: (data.manualInterestCentavos || 0) / 100,
  compromise: (data.manualCompromiseCentavos || 0) / 100,
  taxReliefAvailing: data.taxReliefAvailing === true,
  taxReliefSpecify: data.taxReliefSpecify || "",
  otherRemittances: (data.manualItems?.["29"] || 0) / 100,
  otherRemittancesSpecify: data.otherRemittancesSpecify || "",
  schedule: (data.scheduleI || []).map(newScheduleRow),
});

const FieldError = ({ message }) => (message ? <p role="alert" className="text-xs text-danger-500 mt-1">{message}</p> : null);

const Form1601CTab = ({ client, year, onGoToCompensation, onOpenPostingAccounts }) => {
  const clientId = client?._id;
  const today = manilaToday();
  const [summary, setSummary] = useState(null);
  const [month, setMonth] = useState(null);
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [confirmFile, setConfirmFile] = useState(false);
  const [filedDate, setFiledDate] = useState(today);

  const loadSummary = useCallback(async () => {
    if (!clientId || !year) return;
    try {
      setSummary((await withholdingTaxAPI.get1601cYear(clientId, year)).data);
    } catch (e) {
      setError(e.message);
    }
  }, [clientId, year]);

  // Default month: the first month not yet filed, else December.
  useEffect(() => { setMonth(null); setView(null); loadSummary(); }, [loadSummary]);
  useEffect(() => {
    if (month || !summary) return;
    const open = summary.months.find((m) => m.status !== "filed");
    setMonth((open || summary.months[summary.months.length - 1]).month);
  }, [summary, month]);

  const loadView = useCallback(async () => {
    if (!clientId || !year || !month) return;
    setLoading(true);
    setError(null);
    try {
      const res = await withholdingTaxAPI.get1601c(clientId, year, month);
      setView(res.data);
      setDraft(draftFromView(res.data));
      setFieldErrors({});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, year, month]);
  useEffect(() => { loadView(); }, [loadView]);

  const draftPayload = () => ({
    taxReliefAvailing: draft.taxReliefAvailing,
    taxReliefSpecify: draft.taxReliefAvailing ? draft.taxReliefSpecify.trim() : "",
    scheduleI: draft.schedule.map((r) => ({
      period: r.period, datePaid: r.datePaid || null, bank: r.bank.trim(), number: r.number.trim(),
      taxPaidCentavos: pesosToCentavos(r.taxPaid) || 0, shouldBeTaxDueCentavos: pesosToCentavos(r.shouldBe) || 0,
    })),
    otherRemittancesCentavos: pesosToCentavos(draft.otherRemittances) || 0,
    otherRemittancesSpecify: draft.otherRemittancesSpecify.trim(),
    payment: paymentPayload(PAYMENT_ROWS, draft.payments),
    penaltyAuto: draft.penaltyAuto,
    manualSurchargeCentavos: pesosToCentavos(draft.surcharge) || 0,
    manualInterestCentavos: pesosToCentavos(draft.interest) || 0,
    manualCompromiseCentavos: pesosToCentavos(draft.compromise) || 0,
  });

  const afterChange = async (data) => {
    setView(data);
    await Promise.all([loadSummary(), loadView()]);
  };

  const { runSave, saving, runFile, filing, runAmend, amending } = useReturnActions({
    label: `1601-C · ${MONTHS[(month || 1) - 1]} ${year}`,
    saveRequest: () => {
      // 13A is required when 13 is Yes — checked here before anything is sent.
      if (draft.taxReliefAvailing && !draft.taxReliefSpecify.trim()) {
        return Promise.reject(Object.assign(new Error("Specify the special law or tax treaty (item 13A)"), {
          lineErrors: [{ line: 1, fields: { taxReliefSpecify: "Required when item 13 is Yes" } }],
        }));
      }
      const payload = draftPayload();
      return withholdingTaxAPI.save1601cDraft(clientId, year, month, payload).catch((e) => {
        e.payload = payload;
        throw e;
      });
    },
    fileRequest: (filedAt) => withholdingTaxAPI.file1601c(clientId, year, month, filedAt),
    amendRequest: () => withholdingTaxAPI.amend1601c(clientId, year, month),
    onChanged: afterChange,
    onSaveError: (e) => setFieldErrors(mapFieldErrors(e, e.payload)),
    onFiled: () => setConfirmFile(false),
  });

  const summaryByMonth = useMemo(() => new Map((summary?.months || []).map((m) => [m.month, m])), [summary]);
  const readOnly = view?.readOnly;
  const items = view?.items || {};
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setPayment = (type, patch) => setDraft((d) => ({ ...d, payments: { ...d.payments, [type]: { ...d.payments[type], ...patch } } }));
  const setRow = (id, patch) => setDraft((d) => ({ ...d, schedule: d.schedule.map((r) => (r.id === id ? { ...r, ...patch } : r)) }));

  // Schedule I: filed → the snapshot; draft → the rows being edited.
  const scheduleRows = readOnly ? (view?.scheduleI || []).map(newScheduleRow) : (draft?.schedule || []);
  const scheduleTotal = scheduleRows.reduce((s, r) => s + adjustmentOf(r), 0);
  const noTin = view?.warnings?.find((w) => w.code === "EMPLOYEE_NO_TIN");
  const dole = view?.warnings?.find((w) => w.code === "DOLE_HAZARD_LIST_REMINDER");

  const renderItem = ([no, label]) => {
    const penaltyEditable = !readOnly && (no === "34" || (!draft.penaltyAuto && (no === "32" || no === "33")));
    if (penaltyEditable) {
      const field = PENALTY_FIELD[no];
      return (
        <ItemRow key={no} no={no} label={label}>
          <MoneyInput id={`item-${no}-input`} value={draft[field]} onValueChange={(v) => set({ [field]: v })} className={`${inputClass} w-36 text-right`} />
        </ItemRow>
      );
    }
    if (no === "29") {
      return (
        <ItemRow
          key={no}
          no={no}
          label={label}
          note={readOnly
            ? items["29A"] && <p className="mt-1 ml-[4.25rem] text-xs text-ink-body">Specify: {items["29A"]}</p>
            : (
              <div className="mt-1 ml-[4.25rem]">
                <input aria-label="Item 29 specify" value={draft.otherRemittancesSpecify} onChange={(e) => set({ otherRemittancesSpecify: e.target.value })}
                  placeholder="Specify (e.g. paid via BIR Form 0605)" className={`${inputClass} w-full`} />
                <FieldError message={fieldErrors.otherRemittancesSpecify} />
              </div>
            )}
        >
          {readOnly ? money(items[no]) : (
            <MoneyInput id="item-29-input" value={draft.otherRemittances} onValueChange={(v) => set({ otherRemittances: v })} className={`${inputClass} w-36 text-right`} />
          )}
        </ItemRow>
      );
    }
    if (no === "26" && !readOnly) {
      return (
        <ItemRow key={no} no={no} label={label}
          note={scheduleTotal !== (items["26"] || 0) && <p className="mt-1 ml-[4.25rem] text-xs text-ink-body">From Schedule I below — save the draft to recompute items 27–36.</p>}>
          {money(scheduleTotal)}
        </ItemRow>
      );
    }
    const note = no === "20" && items["20A"]
      ? <p className="mt-1 ml-[4.25rem] text-xs text-ink-body">Specify: {items["20A"]}</p>
      : null;
    return <ItemRow key={no} no={no} label={label} note={note}>{money(items[no])}</ItemRow>;
  };

  return (
    <div>
      {/* Month picker + status */}
      <div role="group" aria-label="Month" className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-12 gap-2 mb-4">
        {MONTHS.map((label, i) => {
          const m = i + 1;
          const s = summaryByMonth.get(m);
          return (
            <button
              key={label}
              type="button"
              aria-pressed={month === m}
              onClick={() => setMonth(m)}
              className={`flex flex-col items-start gap-1 rounded-md border px-2 py-2 text-left text-sm ${month === m ? "border-primary-500 bg-primary-50" : "border-ink-line bg-white"}`}
            >
              <span className="font-medium text-ink">{label}</span>
              {s && (
                <span className="flex flex-wrap gap-1" data-testid={`chips-${m}`}>
                  {statusChips(s, today).map((c) => <Chip key={c} label={c} />)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {error && <p role="alert" className="text-sm text-danger-500 mb-3">{error}</p>}
      {loading && !view && <p className="text-sm text-ink-body">Loading…</p>}

      {view && draft && (
        <div>
          {view.flags?.includes("NEEDS_TAXPAYER_SIZE") && <TaxpayerSizeBanner clientId={clientId} />}

          {dole && QUARTER_END.includes(month) && (
            <div role="note" aria-label="DOLE reminder" className="mb-4 border border-primary-200 bg-primary-50 rounded-lg p-3 flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 text-primary-700" />
              <p className="text-sm text-primary-700">{dole.message}</p>
            </div>
          )}

          {noTin && (
            <div role="status" aria-label="Employees without TIN" className="mb-4 border border-warning-200 bg-warning-50 rounded-lg p-3">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm text-warning-700 flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-0.5" /> {noTin.message}</p>
                {onGoToCompensation && <Button variant="outline" size="sm" onClick={onGoToCompensation}>Go to Compensation</Button>}
              </div>
              <ul className="mt-2 ml-6 list-disc text-sm text-warning-700">
                {noTin.rows.map((r) => (
                  <li key={r._id} data-testid="no-tin-row">{r.employeeName} — {money(r.totalCompCentavos)} compensation, {money(r.taxWithheldCentavos)} withheld</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-medium text-ink">BIR Form 1601-C · {MONTHS[month - 1]} {year}</h3>
              {view.isAmended && <Chip label={readOnly ? "Amended" : "Amendment draft"} />}
              {readOnly && <FiledBadge filedAt={view.filedAt} />}
            </div>
            <div className="flex items-center gap-2">
              {readOnly ? (
                <Button variant="outline" onClick={() => runAmend()} loading={amending}>Amend</Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => runSave()} loading={saving}>Save draft</Button>
                  <Button onClick={() => setConfirmFile(true)}>Mark as filed</Button>
                </>
              )}
            </div>
          </div>

          {!readOnly && (
            <p className="text-xs text-ink-body mb-3">
              Due {bir_date(view.adjustedDueDate)}.{view.penaltyAuto && ` Penalties are shown as if filed today (${bir_date(view.penaltyAsOf)}) and are recomputed with the filing date.`}
            </p>
          )}

          {/* G2: payment journal entry of the filed version. */}
          <ReturnPaymentGl client={client} form="1601c" view={view} onOpenPostingAccounts={onOpenPostingAccounts}
            onChanged={() => Promise.all([loadSummary(), loadView()])} />

          <Part title="Return details">
            {HEADER.map(([no, label, kind]) => <ItemRow key={no} no={no} label={label}>{displayValue(kind, items[no])}</ItemRow>)}
          </Part>

          <Part title="Part I – Background Information">
            {PART_I.map(([no, label, kind]) => <ItemRow key={no} no={no} label={label}>{displayValue(kind, items[no])}</ItemRow>)}
            <ItemRow no="13" label={ITEM_13}>
              {readOnly ? displayValue("bool", items["13"]) : (
                <span role="radiogroup" aria-label="Item 13" className="inline-flex gap-3">
                  {[["Yes", true], ["No", false]].map(([l, v]) => (
                    <label key={l} className="flex items-center gap-1 text-sm text-ink">
                      <input type="radio" name="item13" checked={draft.taxReliefAvailing === v} onChange={() => set({ taxReliefAvailing: v })} className="accent-primary-500" />
                      {l}
                    </label>
                  ))}
                </span>
              )}
            </ItemRow>
            {(readOnly ? items["13"] : draft.taxReliefAvailing) && (
              <ItemRow no="13A" label={ITEM_13A}
                note={<FieldError message={fieldErrors.taxReliefSpecify} />}>
                {readOnly ? displayValue("text", items["13A"]) : (
                  <input aria-label="Item 13A" value={draft.taxReliefSpecify} onChange={(e) => set({ taxReliefSpecify: e.target.value })}
                    placeholder="Special law or treaty *" className={`${inputClass} w-72 ${fieldErrors.taxReliefSpecify ? "border-danger-500" : ""}`} />
                )}
              </ItemRow>
            )}
          </Part>

          <Part title="Part II – Computation of Tax">
            {PART_II.map(renderItem)}
            {!readOnly && (
              <label className="flex items-center gap-2 text-sm text-ink mt-3">
                <input type="checkbox" checked={draft.penaltyAuto} onChange={(e) => set({ penaltyAuto: e.target.checked })} className="h-4 w-4 accent-primary-500" />
                Compute surcharge and interest automatically (the compromise is always entered by hand)
              </label>
            )}
            {!readOnly && <p className="text-xs text-ink-body mt-1">Save the draft to recompute items 26–36.</p>}
          </Part>

          <PaymentDetails title="Part III – Details of Payment" rows={PAYMENT_ROWS} payments={draft.payments} readOnly={readOnly} fieldErrors={fieldErrors} onChange={setPayment} />

          <Part title="Part IV – Schedule I: Adjustment of Taxes Withheld on Compensation from Previous Months">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" aria-label="Schedule I">
                <thead>
                  <tr className="text-xs font-medium text-ink-body text-left">
                    <th className="py-1 pr-2 w-8"><span className="sr-only">Row</span></th>
                    <th className="py-1 pr-2">(1) Previous Month/s (MM/YYYY)</th>
                    <th className="py-1 pr-2">(2) Date Paid (MM/DD/YYYY)</th>
                    <th className="py-1 pr-2">(3) Drawee Bank/Bank Code/Agency</th>
                    <th className="py-1 pr-2">(4) Number</th>
                    <th className="py-1 pr-2 text-right">(5) Tax Paid (Excluding Penalties for the Month)</th>
                    <th className="py-1 pr-2 text-right">(6) Should Be Tax Due for the Month</th>
                    <th className="py-1 pr-2 text-right">(7) Adjustments (6) less (5)</th>
                    {!readOnly && <th className="py-1"><span className="sr-only">Remove</span></th>}
                  </tr>
                </thead>
                <tbody>
                  {scheduleRows.length === 0 && (
                    <tr><td colSpan={9} className="py-3 text-center text-ink-body">No adjustments.</td></tr>
                  )}
                  {scheduleRows.map((r, i) => {
                    const err = (k) => fieldErrors[`scheduleI.${i}.${k}`];
                    return (
                      <tr key={r.id} data-schedule-row={i + 1} className={`border-t border-ink-line ${i >= SCHEDULE_ROWS_ON_FORM ? "bg-ink-canvas" : ""}`}>
                        <td className="py-2 pr-2 text-xs text-ink-body tabular-nums">{i + 1}</td>
                        {readOnly ? (
                          <>
                            <td className="py-2 pr-2">{periodLabel(r.period)}</td>
                            <td className="py-2 pr-2">{bir_date(r.datePaid)}</td>
                            <td className="py-2 pr-2">{r.bank || "—"}</td>
                            <td className="py-2 pr-2">{r.number || "—"}</td>
                            <td className="py-2 pr-2 text-right tabular-nums">{money(pesosToCentavos(r.taxPaid))}</td>
                            <td className="py-2 pr-2 text-right tabular-nums">{money(pesosToCentavos(r.shouldBe))}</td>
                          </>
                        ) : (
                          <>
                            <td className="py-2 pr-2">
                              <input type="month" aria-label={`Schedule row ${i + 1} previous month`} value={r.period} onChange={(e) => setRow(r.id, { period: e.target.value })} className={`${inputClass} ${err("period") ? "border-danger-500" : ""}`} />
                              <FieldError message={err("period")} />
                            </td>
                            <td className="py-2 pr-2">
                              <input type="date" aria-label={`Schedule row ${i + 1} date paid`} value={r.datePaid} onChange={(e) => setRow(r.id, { datePaid: e.target.value })} className={inputClass} />
                              <FieldError message={err("datePaid")} />
                            </td>
                            <td className="py-2 pr-2"><input aria-label={`Schedule row ${i + 1} bank`} value={r.bank} onChange={(e) => setRow(r.id, { bank: e.target.value })} className={inputClass} /></td>
                            <td className="py-2 pr-2"><input aria-label={`Schedule row ${i + 1} number`} value={r.number} onChange={(e) => setRow(r.id, { number: e.target.value })} className={inputClass} /></td>
                            <td className="py-2 pr-2" data-testid={`schedule-paid-${i + 1}`}>
                              <MoneyInput id={`sched-paid-${r.id}`} value={r.taxPaid} onValueChange={(v) => setRow(r.id, { taxPaid: v })} className={`${inputClass} w-32 text-right`} />
                              <FieldError message={err("taxPaidCentavos")} />
                            </td>
                            <td className="py-2 pr-2" data-testid={`schedule-due-${i + 1}`}>
                              <MoneyInput id={`sched-due-${r.id}`} value={r.shouldBe} onValueChange={(v) => setRow(r.id, { shouldBe: v })} className={`${inputClass} w-32 text-right`} />
                              <FieldError message={err("shouldBeTaxDueCentavos")} />
                            </td>
                          </>
                        )}
                        <td className="py-2 pr-2 text-right tabular-nums" data-testid={`schedule-adj-${i + 1}`}>{money(adjustmentOf(r))}</td>
                        {!readOnly && (
                          <td className="py-2">
                            <button type="button" aria-label={`Remove schedule row ${i + 1}`} onClick={() => set({ schedule: draft.schedule.filter((x) => x.id !== r.id) })} className="text-danger-500 hover:text-danger-700">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-ink-line">
                    <td />
                    <td colSpan={6} className="py-2 pr-2 text-sm font-medium text-ink-body">4 Total Adjustment (Sum of Items 1 to 3) → Part II item 26</td>
                    <td className="py-2 pr-2 text-right tabular-nums font-medium text-ink" data-testid="schedule-total">{money(scheduleTotal)}</td>
                    {!readOnly && <td />}
                  </tr>
                </tfoot>
              </table>
            </div>
            {scheduleRows.length > SCHEDULE_ROWS_ON_FORM && (
              <p role="note" className="mt-2 text-xs text-ink-body">
                Attached sheet: rows {SCHEDULE_ROWS_ON_FORM + 1}–{scheduleRows.length} go on {attachedSheets(scheduleRows.length)} attached sheet{attachedSheets(scheduleRows.length) !== 1 ? "s" : ""} (item 4); the total includes them.
              </p>
            )}
            {!readOnly && (
              <Button variant="outline" size="sm" icon={Plus} className="mt-2" onClick={() => set({ schedule: [...draft.schedule, newScheduleRow()] })}>Add row</Button>
            )}
          </Part>

          <FileConfirmModal
            isOpen={confirmFile}
            title={`Mark the ${MONTHS[month - 1]} ${year} 1601-C as filed?`}
            filedDate={filedDate}
            onDateChange={setFiledDate}
            loading={filing}
            onConfirm={() => runFile(filedDate)}
            onCancel={() => setConfirmFile(false)}
          />
        </div>
      )}
    </div>
  );
};

export default Form1601CTab;
